import React from "react";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
} from "recharts";
import ChartTooltip from "./ChartTooltip";
import { LoadPhase, SkeletonCard } from "../../../components/ui";
import {
  CHART_CARD_FOOTPRINT,
  EMPTY_MARK_INSET,
  EmptyMonthAxis,
} from "./EarningsCard";
import { AXIS_TICK, BRAND } from "../chartTheme";
import type { MonthlyClients } from "../types/dashboard.types";

interface NewClientsCardProps {
  /**
   * A count per month of the current year, as the dashboard endpoint sends it.
   * Empty until it arrives, which plots an empty axis rather than a shape the
   * data has not confirmed.
   */
  data: MonthlyClients[];
  /**
   * Whether anyone has joined yet. A workspace with no clients has a series of
   * twelve zeros, and a flat line along the axis reads as a year that went
   * badly rather than as one that has not started — so the card draws the shape
   * a year will take, in grey, and says what fills it.
   *
   * It is not the only thing that puts the card into that state: an empty
   * `data` does too, whoever the workspace belongs to. See `showsAxis`.
   */
  empty?: boolean;
  /**
   * Where the counts are. The card is not drawn until they arrive — a plot of
   * an empty axis is a chart of nothing — and a wait long enough to notice
   * shimmers the whole card in its place.
   */
  phase?: LoadPhase;
}

const NewClientsCard: React.FC<NewClientsCardProps> = ({
  data,
  empty = false,
  phase = "ready",
}) => {
  /**
   * Whether the card shows a mark instead of a plot. The same rule the earnings
   * card beside it goes by: `new_clients_by_month: null` is a year the backend
   * has not counted, which arrives here as no rows, and an area chart handed
   * none draws an empty box. So the flat line stands in whenever there is no
   * series — on a workspace with clients on the books as much as on one that
   * has not started.
   */
  const showsAxis = empty || data.length === 0;

  return phase !== "ready" ? (
    <SkeletonCard phase={phase} className={CHART_CARD_FOOTPRINT} />
  ) : (
    <section
      className={`rounded-md border border-line bg-white p-4 sm:p-5 3xl:p-7 flex flex-col ${CHART_CARD_FOOTPRINT}`}
    >
      {/* A workspace with nobody in it has no series to name, so the card says
          what it is waiting for instead of titling a chart that is not there. */}
      <h2 className="text-[16px] 3xl:text-[19px] font-semibold text-ink">
        {empty ? "Nothing to track yet" : "New clients"}
      </h2>
      {/* Unlike the earnings card, this caption stands over the plot rather
          than over a figure — so where there is no series it says so, since
          "Every month" above a flat line reads as a year of zeros. */}
      <p className="text-[13px] 3xl:text-[15px] text-ink-muted">
        {empty
          ? "Your activity will grow as your practice does."
          : showsAxis
            ? "No data yet"
            : "Every month"}
      </p>

      {showsAxis ? (
        // One flat line along the year: the axis a series will be drawn on,
        // with no rise or fall in it that could be read as a month's count.
        <EmptyMonthAxis
          marks={
            // Inset by what the earnings card leaves beside its outermost
            // stubs, so the two empty charts sitting side by side start and end
            // their marks on the same line.
            <div style={{ paddingInline: EMPTY_MARK_INSET }}>
              <div
                className="h-[3px] w-full rounded-full"
                style={{ backgroundColor: BRAND }}
              />
            </div>
          }
        />
      ) : (
        /* Matches EarningsCard so both charts' month labels share a baseline. */
        <div className="mt-auto pt-5 h-[150px] 3xl:h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={data}
              // The first and last ticks are centred on the axis endpoints, so
              // the side margins must clear half a label ("Jan"/"Dec") or they
              // get clipped by the SVG edge.
              margin={{ top: 8, right: 14, bottom: 0, left: 14 }}
            >
              <defs>
                <linearGradient id="newClientsFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={BRAND} stopOpacity={0.28} />
                  <stop offset="100%" stopColor={BRAND} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="month"
                tickLine={false}
                axisLine={false}
                tick={{ fill: AXIS_TICK, fontSize: 11 }}
                dy={4}
                interval={0}
              />
              <Tooltip
                cursor={{
                  stroke: BRAND,
                  strokeWidth: 1,
                  strokeDasharray: "3 3",
                }}
                content={({ active, payload, label }) => (
                  <ChartTooltip
                    active={active}
                    label={String(label)}
                    swatch={BRAND}
                    value={String(payload?.[0]?.value ?? "")}
                    name="new clients"
                  />
                )}
              />
              <Area
                type="monotone"
                dataKey="clients"
                stroke={BRAND}
                strokeWidth={2}
                fill="url(#newClientsFill)"
                dot={false}
                activeDot={{ r: 4, strokeWidth: 2, stroke: "#FFFFFF" }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
};

export default NewClientsCard;
